import { useCallback, useState } from "react";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select as MUISelect,
} from "@mui/material";

import type { SelectProps, SxProps, Theme } from "@mui/material";

interface SelectOption<T> {
  value: T;
  label: string;
}

export interface Props<T> {
  inputLabel: string;
  defaultValue: T;
  options: SelectOption<T>[];
  selectProps?: SelectProps & { "data-testid"?: string };
  onChange: (value: T) => void;
  disabled?: boolean;
  value?: T;
  sx?: SxProps<Theme>;
}

export function Select<T extends string | number>(props: Props<T>) {
  const {
    defaultValue,
    inputLabel,
    options,
    selectProps = {},
    onChange,
    disabled = false,
    value: v = undefined,
    sx,
  } = props;
  const [internalValue, setInternalValue] = useState<T>(defaultValue);
  const value = v ?? internalValue;

  const handleChange = useCallback(
    (newValue: T) => {
      setInternalValue(newValue);
      onChange(newValue);
    },
    [onChange],
  );

  const menuItems = options.map((o) => (
    <MenuItem key={o.value.toString()} value={o.value.toString()}>
      {o.label}
    </MenuItem>
  ));

  return (
    <FormControl
      sx={[
        { minWidth: 160, width: "100%" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <InputLabel>{inputLabel}</InputLabel>
      <MUISelect
        autoWidth
        {...selectProps}
        label={inputLabel}
        value={value}
        onChange={(e) => {
          handleChange(e.target.value as T);
        }}
        disabled={disabled}
      >
        {menuItems}
      </MUISelect>
    </FormControl>
  );
}
